import { useState, useCallback, useRef, useEffect } from 'react';

export interface WindowManagerReturn {
  openWindows: Record<string, boolean>;
  minimizedWindows: Record<string, boolean>;
  activeWindow: string | null;
  windowOrder: string[];
  windowOpenIndex: Record<string, number>;
  openWindow: (appId: string) => void;
  closeWindow: (appId: string) => void;
  minimizeWindow: (appId: string) => void;
  focusWindow: (appId: string) => void;
  restoreAllWindows: () => void;
}

export function useWindowManager(initialAppId: string = 'guide'): WindowManagerReturn {
  const openIndexCounter = useRef(1);

  const [openWindows, setOpenWindows] = useState<Record<string, boolean>>({
    [initialAppId]: true,
  });
  const [minimizedWindows, setMinimizedWindows] = useState<Record<string, boolean>>({});
  const [activeWindow, setActiveWindow] = useState<string | null>(initialAppId);
  const [windowOrder, setWindowOrder] = useState<string[]>([initialAppId]);
  const [windowOpenIndex, setWindowOpenIndex] = useState<Record<string, number>>({
    [initialAppId]: 0,
  });

  const focusWindow = useCallback((appId: string) => {
    setActiveWindow(appId);
    setWindowOrder(prev => [...prev.filter(id => id !== appId), appId]);
    setMinimizedWindows(prev => ({ ...prev, [appId]: false }));
  }, []);

  const openWindow = useCallback(
    (appId: string) => {
      setOpenWindows(prev => {
        if (prev[appId]) {
          // Already open — just bring to front
          focusWindow(appId);
          return prev;
        }
        const idx = openIndexCounter.current++;
        setWindowOpenIndex(p => ({ ...p, [appId]: idx }));
        setWindowOrder(prevOrder => [...prevOrder, appId]);
        setActiveWindow(appId);
        setMinimizedWindows(p => ({ ...p, [appId]: false }));
        return { ...prev, [appId]: true };
      });
    },
    [focusWindow],
  );

  const closeWindow = useCallback(
    (appId: string) => {
      setOpenWindows(prev => ({ ...prev, [appId]: false }));
      setMinimizedWindows(prev => ({ ...prev, [appId]: false }));
      setWindowOrder(prev => {
        const next = prev.filter(id => id !== appId);
        // Pick the top-most remaining window as the new active
        setActiveWindow(next.at(-1) ?? null);
        return next;
      });
    },
    [],
  );

  const minimizeWindow = useCallback(
    (appId: string) => {
      setMinimizedWindows(prev => {
        const next = { ...prev, [appId]: true };
        // Pick next non-minimized window from the top of the stack
        setWindowOrder(order => {
          const reordered = [appId, ...order.filter(id => id !== appId)];
          const nextActive = [...reordered].reverse().find(id => id !== appId && !next[id]);
          setActiveWindow(nextActive ?? null);
          return reordered;
        });
        return next;
      });
    },
    [],
  );

  const restoreAllWindows = useCallback(() => {
    setMinimizedWindows({});
  }, []);

  const openDetailWindow = useCallback((windowId: string) => {
    const idx = openIndexCounter.current++;
    setWindowOpenIndex(prev => ({ ...prev, [windowId]: idx }));
    setOpenWindows(prev => ({ ...prev, [windowId]: true }));
    setWindowOrder(prev => [...prev.filter(id => id !== windowId), windowId]);
    setActiveWindow(windowId);
    setMinimizedWindows(prev => ({ ...prev, [windowId]: false }));
  }, []);

  // Listen for artwork window open events
  useEffect(() => {
    const handleArtworkWindow = (event: CustomEvent) => {
      const { artwork } = event.detail;
      openDetailWindow(`artwork-${artwork.id}`);
    };

    window.addEventListener('openArtworkWindow', handleArtworkWindow as EventListener);
    return () =>
      window.removeEventListener('openArtworkWindow', handleArtworkWindow as EventListener);
  }, [openDetailWindow]);

  // Listen for blog post detail window open events
  useEffect(() => {
    const handleBlogWindow = (event: CustomEvent) => {
      const { post } = event.detail;
      openDetailWindow(`blog-${post.id}`);
    };

    window.addEventListener('openBlogWindow', handleBlogWindow as EventListener);
    return () =>
      window.removeEventListener('openBlogWindow', handleBlogWindow as EventListener);
  }, [openDetailWindow]);

  // Listen for openApp events from WelcomeWindow
  useEffect(() => {
    const handleOpenApp = (event: CustomEvent) => {
      openWindow(event.detail.appId);
    };
    window.addEventListener('openApp', handleOpenApp as EventListener);
    return () => window.removeEventListener('openApp', handleOpenApp as EventListener);
  }, [openWindow]);

  return {
    openWindows,
    minimizedWindows,
    activeWindow,
    windowOrder,
    windowOpenIndex,
    openWindow,
    closeWindow,
    minimizeWindow,
    focusWindow,
    restoreAllWindows,
  };
}
